import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsCalendar2, BsClock, BsGeoAlt, BsPerson } from 'react-icons/bs';

import { useBookingStore } from '@/store';
import { useBrandStore } from '@/store/brandStore';
import {
  formatAppointmentDate,
  formatAppointmentTimeRange,
} from '@/utils/format';

interface AppointmentInfoProps {
  className?: string;
}

const AppointmentInfo: React.FC<AppointmentInfoProps> = ({
  className = '',
}) => {
  const { t } = useTranslation();
  const {
    storeId,
    availabilityId,
    numberOfGuests,
    getMaxDuration,
    getStartTime,
    getEndTime,
  } = useBookingStore();
  // Include brand in the selector to ensure component re-renders when brand data loads
  const { getBranchName, brand, getBrandName } = useBrandStore();
  // This ensures the component re-renders when brand data changes
  const branchName = brand && storeId ? getBranchName(storeId) : '';
  const locationDisplay =
    brand && branchName ? `${getBrandName()} - ${branchName}` : '';

  const startTime = getStartTime();
  const endTime = getEndTime();
  const maxDuration = getMaxDuration();

  const dateLabel = formatAppointmentDate(availabilityId);
  const timeRangeLabel = formatAppointmentTimeRange({
    startTime,
    endTime,
    maxDurationMinutes: maxDuration,
    hoursShortLabel: t('treatment.hoursShort'),
    minuteLabel: t('common.min'),
  });

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`.trim()}
    >
      <h2 className="text-primary-text mb-2 leading-7 font-semibold">
        {t('payment.appointmentDetails')}
      </h2>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <BsGeoAlt className="text-text-muted size-4" />
          <span className="text-primary-text text-lg leading-6 tracking-[0.08px]">
            {locationDisplay || '-'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <BsCalendar2 className="text-text-muted size-4" />
          <span className="text-primary-text leading-6 tracking-[0.08px]">
            {dateLabel || '-'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <BsClock className="text-text-muted size-4" />
          <span className="text-primary-text leading-6 tracking-[0.08px]">
            {timeRangeLabel || '-'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <BsPerson className="text-text-muted size-4" />
          <span className="text-primary-text leading-6 tracking-[0.08px]">
            {numberOfGuests}{' '}
            {numberOfGuests === 1 ? t('payment.guest') : t('payment.guests')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AppointmentInfo;
